import { KeyboardEvent, MouseEvent, useMemo, useState } from 'react';
import DoneIcon from '@mui/icons-material//Done';
import { Avatar, CardHeader, MenuItem, Tooltip } from '@mui/material';
import Chip from '@mui/material/Chip';
import { isNil } from 'lodash';
import { USER_ROLES, USER_ROLES_MAP_VALUES_TO_LABELS } from 'common/constants/user';
import { UserCardProps } from './UserCard.props';
import { StyledArrowDropDown, StyledButtonBadge, StyledGridContainer, StyledMenuList } from './UserCard.styles';

const UserCard = ({
  name,
  email,
  lastLogin,
  pictureUrl,
  roleType,
  setSelectedRole,
  disabled,
  isPrimaryUser,
}: UserCardProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const handleBadgeClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleBadgeKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleTooltipOpen = () => {
    setTooltipOpen(true);
  };

  const handleTooltipClose = () => {
    setTooltipOpen(false);
  };

  const handleRoleSelect = (event: MouseEvent<HTMLLIElement>) => {
    const value = Number((event.target as HTMLLIElement).getAttribute('value'));
    const newRole = USER_ROLES_MAP_VALUES_TO_LABELS[value as keyof typeof USER_ROLES_MAP_VALUES_TO_LABELS]?.value;
    if (newRole !== null && newRole !== undefined && setSelectedRole) {
      setSelectedRole({ roleType: newRole });
    }
    handleMenuClose();
  };

  const roleLabel = useMemo(() => !isNil(roleType)
    ? USER_ROLES_MAP_VALUES_TO_LABELS[roleType as keyof typeof USER_ROLES_MAP_VALUES_TO_LABELS].label.toUpperCase()
    : '', [roleType]);

  return (
    <StyledGridContainer>
      <div>
        <Avatar alt={name} src={pictureUrl} style={{ width: '4.5rem', height: '4.5rem' }} />
      </div>
      <div>
        <Tooltip
          title={disabled ? 'You cannot change your own role' : ''}
          placement="right"
          arrow
          open={tooltipOpen}
          onOpen={handleTooltipOpen}
          onClose={handleTooltipClose}
          disableFocusListener
          disableTouchListener>
          <StyledButtonBadge
            disabled={disabled || isNil(roleType)}
            type="button"
            onClick={handleBadgeClick}
            onKeyDown={handleBadgeKeyDown}
            onMouseEnter={handleTooltipOpen}
            onMouseLeave={handleTooltipClose}
            aria-label="Select role"
            style={{ minHeight: '2.25rem' }}>
            {roleLabel}
            <StyledArrowDropDown />
          </StyledButtonBadge>
        </Tooltip>
        {isPrimaryUser && <Chip label="Primary Contact" color="primary" deleteIcon={<DoneIcon />} />}
      </div>
      <CardHeader
        title={<div style={{ textAlign: 'center' }}>{name || ''}</div>}
        subheader={
          <div style={{ textAlign: 'center' }}>
            <p style={{ marginTop: '0.375rem' }}>{email || ''}</p>
            <p style={{ marginTop: '0.375rem' }}>{lastLogin || ''}</p>
          </div>
        }
        titleTypography={{ align: 'center' }}
        subheaderTypography={{ align: 'center' }}
      />
      <StyledMenuList anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {Object.values(USER_ROLES).map(r => (
          <MenuItem key={r.value} value={r.value} onClick={handleRoleSelect}>
            {r.label}
          </MenuItem>
        ))}
      </StyledMenuList>
    </StyledGridContainer>
  );
};

export default UserCard;
